import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, List, PaginationBar, Text, usePaginationParams } from '@common-fe/common-fe';

import { useGetEmployeeId } from '@/modules/employee/Employee/hooks';

import { useClaimsSearchParticipantQuery, useGetClaimServicesQuery } from '../../../../queries';

import AssociateWithClaimsListHeader from './AssociateWithClaimsListHeader';
import AssociateWithClaimsListItem from './AssociateWithClaimsListItem';

const DEFAULT_PAGE_SIZE = 10;

interface Props {
  onSelectClaims: (claimsIds: string[]) => void;
  isMobile?: boolean;
}

const AssociateWithClaimsList: React.FC<Props> = ({ onSelectClaims, isMobile }) => {
  const { page, perPage, setPage } = usePaginationParams(DEFAULT_PAGE_SIZE);
  const { claims, total, isLoading } = useClaimsSearchParticipantQuery({ page, perPage });
  const [selectedClaims, setSelectedClaims] = useState<Set<string>>(() => new Set());
  const employeeId = useGetEmployeeId();
  

  const claimsServicesIds = useMemo(() => {
    const servicesIds = (claims?.map((claim) => claim?.serviceId)?.filter(Boolean) as string[]) || [];
    return [...new Set(servicesIds)];
  }, [claims]);

  const { services, isLoading: isServicesLoading } = useGetClaimServicesQuery({
    serviceIds: claimsServicesIds,
    enabled: !!claimsServicesIds?.length,
  });

  const claimsWithServices = useMemo(
    () =>
      claims?.map((claim) => {
        const claimService = services?.find((service) => service?.id === claim?.serviceId);
        return {
          ...claim,
          ...claimService,
        };
      }),
    [claims, services]
  );

  const isAllClaimsSelected = useMemo(() => {
    const claimsIdsFromPage = claims?.map((claim) => claim?.claimId).filter(Boolean) as string[];
    return !!claimsIdsFromPage?.length && claimsIdsFromPage?.every((claimId) => selectedClaims.has(claimId));
  }, [claims, selectedClaims]);

  const handleSelectClaim = useCallback((claimId: string) => {
    setSelectedClaims((prev) => {
      const currentSelectedClaims = new Set(prev);
      if (currentSelectedClaims.has(claimId)) {
        currentSelectedClaims.delete(claimId);
      } else {
        currentSelectedClaims.add(claimId);
      }
      return currentSelectedClaims;
    });
  }, []);

  const handleSelectAllClaimsFromPage = useCallback(() => {
    const claimsIdsFromPage = claims?.map((claim) => claim?.claimId).filter(Boolean) as string[];
    setSelectedClaims((prev) => {
      if (isAllClaimsSelected) {
        const currentSelectedClaims = new Set(prev);
        claimsIdsFromPage.forEach((selectedClaim) => currentSelectedClaims.delete(selectedClaim));
        return new Set(currentSelectedClaims);
      } else {
        return new Set([...prev, ...claimsIdsFromPage]);
      }
    });
  }, [claims, isAllClaimsSelected]);

  useEffect(() => {
    onSelectClaims([...selectedClaims]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClaims]);

  const children = useMemo(() => {
    if (isLoading || isServicesLoading) {
      return <List.Preloader testId="associate-with-claims-list_preloader" />;
    }

    return claimsWithServices?.map((claim) => (
      <AssociateWithClaimsListItem
        key={claim?.claimId}
        employeeId={employeeId}
        claimId={claim?.claimId}
        providerName={claim?.providerName}
        categoryType={claim?.categoryType}
        serviceName={claim?.name}
        serviceOn={claim?.serviceOn}
        amount={claim?.requestedAmount}
        isChecked={!!claim?.claimId && selectedClaims.has(claim?.claimId)}
        onCheck={handleSelectClaim}
        isMobile={isMobile}
      />
    ));
  }, [claimsWithServices, isLoading, isServicesLoading, selectedClaims, handleSelectClaim, isMobile, employeeId]);

  return (
    <Box>
      <Box direction="row" align="center" justify="between">
        <Text weight={700} size="large" data-testId="associate-with-claims-list_total">
          Claims: {total}
        </Text>

        {isMobile ? (
          <Box direction="row" align="center" gap="spacing8">
            <Text size="large">Select All</Text>
            <Checkbox
              testId="associate-with-claims-list_select-all-claims"
              checked={isAllClaimsSelected}
              onChange={handleSelectAllClaimsFromPage}
            />
          </Box>
        ) : null}
      </Box>

      <Box margin={{ top: isMobile ? 'spacing8' : 'spacing16' }}>
        <List>
          {isMobile ? null : (
            <AssociateWithClaimsListHeader
              isAllClaimsSelected={isAllClaimsSelected}
              onSelectAllClaimsFromPage={handleSelectAllClaimsFromPage}
            />
          )}

          {children}
        </List>

        {!!claims?.length && (
          <PaginationBar total={total} page={page} onChangePage={setPage} pageSize={perPage} hidePageSize />
        )}
      </Box>
    </Box>
  );
};

export default AssociateWithClaimsList;
